/**
 * Voice billing: turn what a cashier says into item requests, e.g.
 *   "two kilo onion and half kilo tomato, 3 milk"
 *   "ரெண்டு கிலோ வெங்காயம் அரை கிலோ தக்காளி"   (Tamil)
 *   "rendu kilo vengayam, arai kilo thakkali"    (Tamil, romanised)
 *   "दो किलो प्याज़ और आधा किलो टमाटर"            (Hindi; also romanised "do kilo pyaz")
 * Each request is { words, qty (in units), unit? }; matching against the catalog happens in the app.
 */
export interface VoiceItem { words: string; qty: number | null; unit: 'kg' | 'g' | 'l' | 'ml' | 'pc' | null }

const NUMBERS: Record<string, number> = {
  // English
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, fifteen: 15, twenty: 20, dozen: 12,
  // Tamil (script and romanised)
  'ஒரு': 1, 'ஒன்று': 1, 'ஒண்ணு': 1, 'இரண்டு': 2, 'ரெண்டு': 2, 'மூன்று': 3, 'மூணு': 3, 'நான்கு': 4, 'நாலு': 4,
  'ஐந்து': 5, 'அஞ்சு': 5, 'ஆறு': 6, 'ஏழு': 7, 'எட்டு': 8, 'ஒன்பது': 9, 'பத்து': 10, 'பன்னிரண்டு': 12, 'இருபது': 20,
  oru: 1, onnu: 1, ondru: 1, rendu: 2, irandu: 2, moonu: 3, moondru: 3, naalu: 4, nalu: 4, anju: 5, aindhu: 5,
  aaru: 6, ezhu: 7, ettu: 8, onbadhu: 9, pathu: 10, patthu: 10,
  // Hindi (script and romanised)
  'एक': 1, 'दो': 2, 'तीन': 3, 'चार': 4, 'पांच': 5, 'पाँच': 5, 'छह': 6, 'छः': 6, 'सात': 7, 'आठ': 8, 'नौ': 9, 'दस': 10,
  'बारह': 12, 'पंद्रह': 15, 'बीस': 20, 'दर्जन': 12,
  ek: 1, do: 2, teen: 3, char: 4, chaar: 4, paanch: 5, panch: 5, chhe: 6, saat: 7, aath: 8, nau: 9, das: 10, barah: 12, bees: 20, darjan: 12,
};
const FRACTIONS: Record<string, number> = {
  half: 0.5, quarter: 0.25, 'கால்': 0.25, 'அரை': 0.5, 'முக்கால்': 0.75,
  kaal: 0.25, kal: 0.25, arai: 0.5, ara: 0.5, mukkal: 0.75, mukkaal: 0.75,
  'आधा': 0.5, 'आधी': 0.5, 'पाव': 0.25, 'पौना': 0.75, 'डेढ़': 1.5, 'डेढ': 1.5, 'ढाई': 2.5,
  aadha: 0.5, adha: 0.5, aadhi: 0.5, paav: 0.25, pav: 0.25, pauna: 0.75, dedh: 1.5, dhai: 2.5, dhaai: 2.5,
};
const UNITS: Record<string, VoiceItem['unit']> = {
  kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg', 'கிலோ': 'kg', 'किलो': 'kg',
  g: 'g', gm: 'g', gms: 'g', gram: 'g', grams: 'g', 'கிராம்': 'g', 'ग्राम': 'g',
  l: 'l', litre: 'l', liter: 'l', litres: 'l', liters: 'l', 'லிட்டர்': 'l', 'लीटर': 'l', 'लिटर': 'l',
  ml: 'ml', 'மில்லி': 'ml', 'मिली': 'ml',
  packet: 'pc', packets: 'pc', pack: 'pc', piece: 'pc', pieces: 'pc', pc: 'pc', pcs: 'pc', 'பாக்கெட்': 'pc', 'பீஸ்': 'pc', 'पैकेट': 'pc', 'पीस': 'pc',
};
const FILLERS = new Set(['and', 'also', 'then', 'please', 'of', 'add', 'give', 'மற்றும்', 'அப்புறம்', 'கொடு', 'kodu', 'apram', 'appuram', 'um',
  'और', 'फिर', 'भी', 'का', 'की', 'के', 'aur', 'phir', 'bhi']);

/** Split a spoken sentence into item requests. */
export function parseVoice(text: string): VoiceItem[] {
  const tokens = text.toLowerCase().replace(/[.,;!?]+/g, ' , ').split(/\s+/).filter(Boolean);
  const items: VoiceItem[] = [];
  let qty: number | null = null;
  let unit: VoiceItem['unit'] = null;
  let words: string[] = [];
  const flush = () => {
    if (words.length) items.push({ words: words.join(' '), qty, unit });
    qty = null; unit = null; words = [];
  };
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === ',' || FILLERS.has(t)) { if (words.length) flush(); continue; }
    const num = /^\d+(\.\d+)?$/.test(t) ? Number(t) : NUMBERS[t];
    const frac = FRACTIONS[t];
    if (num != null || frac != null) {
      // A number after words starts the next item ("onion 2 kilo tomato").
      if (words.length) flush();
      if (frac != null) qty = (qty ?? 0) + frac;
      else qty = (qty ?? 0) + num!;
      // "one and a half", "ஒன்றரை"-style: number + and + half
      if (tokens[i + 1] === 'and' && (tokens[i + 2] === 'half' || (tokens[i + 2] === 'a' && tokens[i + 3] === 'half'))) {
        qty += 0.5;
        i += tokens[i + 2] === 'a' ? 3 : 2;
      }
      continue;
    }
    // Hindi "सवा दो" (a quarter more than two).
    if ((t === 'सवा' || t === 'sawa' || t === 'savaa') && NUMBERS[tokens[i + 1]] != null) {
      if (words.length) flush();
      qty = NUMBERS[tokens[i + 1]] + 0.25;
      i += 1;
      continue;
    }
    if (UNITS[t] && (qty != null || !words.length)) { unit = UNITS[t]; continue; }
    // Tamil compound like "ஒன்றரை" (one and a half) or "ரெண்டரை".
    const m = /^(.+)ரை$/.exec(t);
    if (m && NUMBERS[m[1] + 'று'] != null) { qty = NUMBERS[m[1] + 'று'] + 0.5; continue; }
    words.push(t);
  }
  flush();
  return items;
}

/** Quantity in milli-units for a product unit, from what was said ("half kilo" of a kg item → 500). */
export function voiceQty(item: VoiceItem, productUnit: string): number | null {
  if (item.qty == null) return null;
  const q = item.qty;
  if (productUnit === 'kg' || productUnit === 'l') {
    if (item.unit === 'g' || item.unit === 'ml') return Math.round(q);           // 500 gram → 0.5 kg = 500 milli
    return Math.round(q * 1000);
  }
  if (productUnit === 'g' || productUnit === 'ml') {
    if (item.unit === 'kg' || item.unit === 'l') return Math.round(q * 1_000_000);
    return Math.round(q * 1000);
  }
  return Math.max(1000, Math.round(q) * 1000);                                    // pieces and packs: whole numbers
}
